import type { UseToastOptions } from '@chakra-ui/react';
import { Box, Button, Center, ChakraProvider, Heading, Slide, Spinner, Text } from '@chakra-ui/react';
import { withEmotionCache } from '@emotion/react';
import { json, redirect } from '@remix-run/node';
import { Links, LiveReload, Meta, Outlet, Scripts, ScrollRestoration, useCatch, useLoaderData, useLocation, useRouteLoaderData, useTransition } from '@remix-run/react';
import React, { useEffect, useState } from 'react';
import reactDatePickerCSS from 'react-datepicker/dist/react-datepicker.css';

import { LayoutBase as Layout, Toaster } from '~/components';
import fonts from '~/styles/fonts.css';
import reactDatePickerChakraCSS from '~/styles/react-datepicker.css';
import tailwindCSS from '~/styles/tailwind.css';
import theme from '~/styles/theme';
import ClientStyleContext from './context.client';
import ServerStyleContext from './context.server';
import { getOptionalUser } from './services/auth.server';

import lightboxCSS from 'yet-another-react-lightbox/styles.css';

import type { LoaderArgs, MetaFunction, SerializeFrom } from '@remix-run/node';
import { useSpinDelay } from 'spin-delay';
import { commitSession, getSession } from './services/session.server';
import type { User } from './types';

import rdtStylesheet from 'remix-development-tools/stylesheet.css';

import { prisma } from '~/services/db.server';

import { AssistantRuntimeProvider, useLocalRuntime } from '@assistant-ui/react';
import { ClientOnly } from 'remix-utils';
import { AssistantModal } from '~/components/assistant-ui/assistant-modal';
import { TooltipProvider } from '~/components/ui/tooltip';
import { mockRagAdapter } from '~/lib/mock-rag-adapter';
import { authenticator } from '~/services/auth.server';
import { getUserByEmailOrCUAA } from './services/db/queries.server';

// we don't want to show the loading indicator on page load
let firstRender = true;

function getContextFromPath(pathname: string): string {
  const pathSegments = pathname.replace(/^\//, '').split('/');

  const contextMappings: Record<string, string> = {
    'dashboard-partenariato': 'srg07',
    srg07: 'srg07',
    'user-beneficiario': 'general',
    'procedimento-documento-convenzione-beneficiario': 'general',
    'relazione-tecnica': 'general',
    'domande-beneficiario': 'general'
  };

  const fullPath = pathSegments.join('/');
  if (contextMappings[fullPath]) {
    return contextMappings[fullPath];
  }

  const firstSegment = pathSegments[0];
  if (contextMappings[firstSegment]) {
    return contextMappings[firstSegment];
  }

  if (pathSegments.length > 1) {
    const secondSegment = pathSegments[1];
    if (contextMappings[secondSegment]) {
      return contextMappings[secondSegment];
    }
  }

  return 'general';
}

function PageLoadingSpinner() {
  const transition = useTransition();
  const showLoader = useSpinDelay(Boolean(transition.state !== 'idle'), {
    delay: 1000,
    minDuration: 800
  });

  React.useEffect(() => {
    if (firstRender) return;
    if (transition.state === 'idle') return;
    if (transition.state === 'submitting') return;
  }, [transition.state]);

  React.useEffect(() => {
    firstRender = false;
  }, []);

  return (
    <Slide direction="bottom" in={showLoader} style={{ zIndex: 10 }}>
			<Box float={'right'} width={'100px'} p="4" color="white" m="8" bg="blue.500" rounded="lg" shadow="lg">
				<Center>
					<Spinner />
				</Center>
			</Box>
		</Slide>);

}

// Chatbot Widget Component
function ChatbotWidget({ urlChatbot }: {urlChatbot: string;}) {
  const location = useLocation();
  const [chatbotOpen, setChatbotOpen] = useState(false);
  const [welcomeShown, setWelcomeShown] = useState(false);
  const [frameLoaded, setFrameLoaded] = useState(false);
  const [showNotification, setShowNotification] = useState(true);

  // Get context based on current location
  const currentContext = getContextFromPath(location.pathname);

  // Generate the chatbot URL with dynamic context
  const chatbotUrl = `${urlChatbot}/?context=${currentContext}`;

  useEffect(() => {
    // Show welcome message after 3 seconds
    const timer = setTimeout(() => {
      if (!welcomeShown && !chatbotOpen) {
        setWelcomeShown(true);
      }
    }, 60000);

    return () => clearTimeout(timer);
  }, [welcomeShown, chatbotOpen]);

  useEffect(() => {
    // Auto-hide welcome message after 8 seconds
    if (welcomeShown && !chatbotOpen) {
      const timer = setTimeout(() => {
        setWelcomeShown(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [welcomeShown, chatbotOpen]);

  useEffect(() => {
    // Hide notification badge after user interaction
    const handleClick = () => {
      setTimeout(() => {
        if (!chatbotOpen) {
          setShowNotification(false);
        }
      }, 5000);
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [chatbotOpen]);

  // Reset frame loaded state when context changes
  useEffect(() => {
    if (chatbotOpen) {
      setFrameLoaded(false);
    }
  }, [currentContext, chatbotOpen]);

  const toggleChatbot = () => {
    setChatbotOpen(!chatbotOpen);
    if (!chatbotOpen) {
      setWelcomeShown(false);
      setShowNotification(false);
    }
  };

  const closeWelcome = () => {
    setWelcomeShown(false);
  };

  return (
    <Box position="fixed" bottom="20px" right="20px" zIndex="1000" fontFamily="'Inter', system-ui, -apple-system, sans-serif">
			{/* Welcome Message */}
			{welcomeShown &&
      <Box
        position="absolute"
        bottom="90px"
        right="80px"
        bg="white"
        p="4"
        borderRadius="12px"
        boxShadow="0 8px 25px rgba(0, 0, 0, 0.15)"
        maxWidth="280px"
        transform={welcomeShown ? 'translateY(0)' : 'translateY(10px)'}
        opacity={welcomeShown ? 1 : 0}
        transition="all 0.3s ease"
        border="1px solid #e5e7eb"
        _after={{
          content: '""',
          position: 'absolute',
          bottom: '-8px',
          right: '20px',
          width: 0,
          height: 0,
          borderLeft: '8px solid transparent',
          borderRight: '8px solid transparent',
          borderTop: '8px solid white'
        }}>

					<Button
          position="absolute"
          top="0.5rem"
          right="0.5rem"
          bg="none"
          border="none"
          color="#999"
          cursor="pointer"
          fontSize="1rem"
          p="0"
          minW="auto"
          h="auto"
          onClick={closeWelcome}>
        </Button>
					<Heading as="h4" color="#667eea" fontSize="0.9rem" mb="2">
						👋 Hai domande sul SIARC?
					</Heading>
					<Box fontSize="0.8rem" color="#666">
						Il nostro assistente virtuale è qui per aiutarti con accessi, deleghe e procedure.
					</Box>
				</Box>
      }

			{/* Chatbot Container */}
			<Box
        position="absolute"
        bottom="80px"
        right="0"
        width="380px"
        height="600px"
        bg="white"
        borderRadius="16px"
        boxShadow="0 20px 60px rgba(0, 0, 0, 0.15)"
        transform={chatbotOpen ? 'scale(1) translateY(0)' : 'scale(0) translateY(20px)'}
        transformOrigin="bottom right"
        transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
        opacity={chatbotOpen ? 1 : 0}
        overflow="hidden"
        border="1px solid rgba(0, 0, 0, 0.1)"
        sx={{
          '@media (max-width: 768px)': {
            width: 'calc(100vw - 20px)',
            height: 'calc(100vh - 100px)',
            right: '10px',
            left: '10px'
          }
        }}>

				{/* Chatbot Header */}
				<Box background="linear-gradient(135deg, #28a745 0%, #0584d9 100%)" color="white" p="4" display="flex" alignItems="center" gap="3" borderRadius="16px 16px 0 0">
					<Box width="40px" height="40px" bg="rgba(255, 255, 255, 0.2)" borderRadius="50%" display="flex" alignItems="center" justifyContent="center" fontSize="20px">
						🌾
					</Box>
					<Box>
						<Heading as="h3" fontSize="1rem" m="0" fontWeight="600">
							Assistente SIARC
						</Heading>
						<Box fontSize="0.8rem" m="0" opacity="0.9">
							{!frameLoaded ? 'Caricamento...' : `Online - Contesto: ${currentContext}`}
						</Box>
					</Box>
				</Box>

				{/* Chatbot Iframe */}
				<Box
          as="iframe"
          width="100%"
          height="calc(100% - 72px)"
          border="none"
          bg="white"
          src={chatbotOpen ? chatbotUrl : 'about:blank'}
          title="Marinell.AI"
          onLoad={() => setFrameLoaded(true)}
          key={currentContext} // Force re-render when context changes
        />
			</Box>

			{/* Trigger Button */}
			<Button
        width="60px"
        height="60px"
        background="linear-gradient(135deg, #28a745 0%, #0584d9 100%)"
        borderRadius="50%"
        border="none"
        cursor="pointer"
        boxShadow="0 4px 20px rgba(102, 126, 234, 0.4)"
        transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
        display="flex"
        alignItems="center"
        justifyContent="center"
        color="white"
        fontSize="24px"
        position="relative"
        overflow="hidden"
        _hover={{
          transform: 'scale(1.1)',
          boxShadow: '0 8px 30px rgba(102, 126, 234, 0.6)'
        }}
        _active={{
          transform: 'scale(0.95)'
        }}
        onClick={toggleChatbot}>

				{/* Notification Badge */}
				{showNotification && !chatbotOpen &&
        <Box
          position="absolute"
          top="-5px"
          right="-5px"
          width="20px"
          height="20px"
          bg="#ef4444"
          borderRadius="50%"
          display="flex"
          alignItems="center"
          justifyContent="center"
          color="white"
          fontSize="12px"
          fontWeight="bold"
          border="2px solid white"
          animation="pulse 2s infinite">

						1
					</Box>
        }

				{/* Chat Icon */}
				<Box transform={chatbotOpen ? 'rotate(-45deg) scale(0)' : 'rotate(0deg) scale(1)'} transition="transform 0.3s ease">
					💬
				</Box>

				{/* Close Icon */}
				<Box position="absolute" transform={chatbotOpen ? 'rotate(0deg) scale(1)' : 'rotate(45deg) scale(0)'} transition="transform 0.3s ease">
					✕
				</Box>
			</Button>

			{/* Add pulse animation */}
			<style>
				{`
					@keyframes pulse {
						0% { transform: scale(1); }
						50% { transform: scale(1.1); }
						100% { transform: scale(1); }
					}
				`}
			</style>
		</Box>);

}

async function getChatbotConfig() {
  try {
    // Using Prisma to query the config table
    // Adjust the model name according to your Prisma schema
    const config = await prisma.config.findUnique({
      where: {
        key: 'chatbot_enabled'
      },
      select: {
        value: true
      }
    });

    const url = await prisma.config.findUnique({
      where: {
        key: 'url_chatbot'
      },
      select: {
        value: true
      }
    });

    // Return true if config exists and value is 'true', false otherwise
    return {
      chatbotEnabled: config?.value === 'true' || false,
      urlChatbot: url?.value || 'localhost:8501'
    };
  } catch (error) {
    console.error('Error fetching chatbot config:', error);
    // Default to false if there's an error
    return {
      chatbotEnabled: false,
      urlChatbot: 'localhost:8501'
    };
  }
}

export function links() {
  return [
  { rel: 'stylesheet', href: tailwindCSS },
  { rel: 'stylesheet', href: reactDatePickerCSS },
  { rel: 'stylesheet', href: reactDatePickerChakraCSS },
  { rel: 'stylesheet', href: fonts },
  // { rel: "stylesheet", href: calendarCSS },
  { rel: 'stylesheet', href: lightboxCSS },
  ...(rdtStylesheet ? [{ rel: 'stylesheet', href: rdtStylesheet }] : [])
  // { rel: 'stylesheet', href: bootstrapItaliaCSS },
  ];
}

export async function loader({ request }: LoaderArgs) {
  const user = await getOptionalUser(request);
  const session = await getSession(request.headers.get('Cookie'));

  if (!user) {
    // Autologin if not logged

    let account = await prisma.account.findFirst({
      where: {
        email: 'nellodan@gmail.com'
      }
    });

    if (account === null) throw new Error('User not found');

    if (account.email === null) throw new Error('User not found');

    let userDemo = await getUserByEmailOrCUAA(account.email);

    if (userDemo === null) throw new Error('User not found');

    session.set(authenticator.sessionKey, userDemo);

    return redirect(`/`, {
      headers: {
        'Set-Cookie': await commitSession(session)
      }
    });
  }

  const config = await getChatbotConfig();

  const toastMessage = session.get('toastMessage') as UseToastOptions;

  const latestKey = '';

  const ENV = {
    MODE: process.env.NODE_ENV
  };

  if (!toastMessage) {
    return json({
      toastMessage,
      user,
      chatbotEnabled: config?.chatbotEnabled || false,
      urlChatbot: config?.urlChatbot || 'localhost:8501',
      ENV,
      latestKey
    });
  }

  return json(
    {
      toastMessage,
      user,
      chatbotEnabled: config?.chatbotEnabled || false,
      urlChatbot: config?.urlChatbot || 'localhost:8501',
      ENV,
      latestKey
    },
    { headers: { 'Set-Cookie': await commitSession(session) } }
  );
}

export const meta: MetaFunction = () => {
  return { title: 'SIARC 1.0' };
};

export function CatchBoundary() {
  let caught = useCatch();

  let message;
  switch (caught.status) {
    case 401:
      message = <Text>Oops! Looks like you tried to visit a page that you do not have access to.</Text>;
      break;
    case 404:
      message = <Text>Oops! Looks like you tried to visit a page that does not exist.</Text>;
      break;

    default:
      throw new Error(caught.data || caught.statusText);
  }

  return (
    <Document title={`${caught.status} - ${caught.statusText}`}>
			<Layout>
				<Heading as="h1">
					{caught.status}: {caught.statusText}
				</Heading>
				{message}
			</Layout>
		</Document>);

}

interface DocumentProps {
  children: React.ReactNode;
  title?: string;
}

const Document = withEmotionCache(({ children, title }: DocumentProps, emotionCache) => {
  const serverSyleData = React.useContext(ServerStyleContext);
  const clientStyleData = React.useContext(ClientStyleContext);

  const data = useRouteLoaderData('root') as SerializeFrom<typeof loader>;

  // Only executed on client
  React.useEffect(() => {
    // re-link sheet container
    emotionCache.sheet.container = document.head;
    // re-inject tags
    const tags = emotionCache.sheet.tags;
    emotionCache.sheet.flush();
    tags.forEach((tag) => {
      ;(emotionCache.sheet as any)._insertTag(tag);
    });
    // reset cache to reapply global styles
    clientStyleData.reset();
  }, []);

  let colorMode = 'light';

  return (
    <html lang="en" data-theme={colorMode} style={{ colorScheme: colorMode }}>
			<head>
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width,initial-scale=1" />

				{title ? <title>{title}</title> : null}
				<Meta />
				<Links />
				{serverSyleData?.map(({ key, ids, css }) =>
        <style
          key={key}
          data-emotion={`${key} ${ids.join(' ')}`}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: css }} />

        )}
			</head>
			<body className={`chakra-ui-${colorMode}`}>
				<ChakraProvider theme={theme}>
					<PageLoadingSpinner />

					<Toaster />
					{children}
				</ChakraProvider>
				<ScrollRestoration />
				<script
          dangerouslySetInnerHTML={{
            __html: `window.ENV = ${JSON.stringify(data?.ENV)}`
          }} />

				<Scripts />
				{process.env.NODE_ENV === 'development' &&
        <>
						<LiveReload />
					</>
        }
			</body>
		</html>);

});

function AssistantProvider({ children }: {children: React.ReactNode;}) {
  const runtime = useLocalRuntime(mockRagAdapter);
  return <AssistantRuntimeProvider runtime={runtime}>{children}</AssistantRuntimeProvider>;
}

function ProcedimentoAssistant() {
  const location = useLocation();
  const match = location.pathname.match(/^\/procedimenti\/[^/]+\/(\d+)/);
  if (!match) return null;

  return (
    <ClientOnly key={match[1]}>
			{() =>
      <AssistantProvider>
					<div className="assistant-ui-root">
						<TooltipProvider delayDuration={150}>
							<AssistantModal />
						</TooltipProvider>
					</div>
				</AssistantProvider>
      }
		</ClientOnly>);

}

export function App() {
  let { user, chatbotEnabled, urlChatbot, latestKey } = useLoaderData<typeof loader>();

  let utente = user as User;

  return (
    <Document>
			<Layout user={utente} latestDocPrivacy={latestKey}>
				<Outlet />
			</Layout>
			<ProcedimentoAssistant />
		</Document>);

}

export default App;